import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { api } from '../api';
import { AvatarUsuario } from './AvatarUsuario';
import { RefinoSplit } from './RefinoSplit';
import { GRUPOS, TIPOS_PECA, TIPOS_PECA_BY_KEY, sugestaoPorTipoPrazo, type TipoPeca } from '../tiposPeca';
import { useTransicao, useTransicoesPermitidas, useMe, usePecasStatus, usePecasStatusRapido, useExcluirPrazo, useIniciarPipeline, useAprovarMapa, usePlanoPro, usePode, useRenderizarPeca, useSetEtapa, useRecentlyMoved } from '../hooks';
import { ProgressoPipeline } from './ProgressoPipeline';
import { BlocosLivePreview } from './BlocosLivePreview';
import type { EndpointTransicao, EtapaPipeline, PrazoCard } from '../types';
import { AVATAR_COR, BG_TAILWIND, VAR_COR, fmtData } from '../utils';
import { BadgeEtiqueta } from './BadgeEtiqueta';

interface Props {
  card: PrazoCard;
  onClick?: () => void;
  /** Quando true (default), o card é arrastável (Fase 8). */
  draggable?: boolean;
}

const ROTULO_ETAPA: Record<EtapaPipeline, string> = {
  fila:          '⏸️ Na fila',
  preparo:       '⚙️ Preparo (split/OCR/classify)',
  mapa:          '✏️ Refinamento (rascunho pronto)',
  mapa_aprovado: '✍️ Mapa aprovado',
  redigindo:     '✍️ Redigindo a peça',
  revisao:       '🔎 Em revisão',
  concluido:     '✓ Concluído',
};
function rotuloEtapa(e: EtapaPipeline | null): string {
  return e ? ROTULO_ETAPA[e] : '';
}

const ENDPOINT_DE_NOME: Record<string, EndpointTransicao> = {
  triar:               'triar',
  devolver_triagem:    'devolver-triagem',
  assumir:             'assumir',
  marcar_pronto:       'marcar-pronto',
  protocolar:          'protocolar',
  desfazer_triagem:    'desfazer-triagem',
  desfazer_assumir:    'desfazer-assumir',
  desfazer_pronto:     'desfazer-pronto',
  desfazer_protocolo:  'desfazer-protocolo',
};

export function CardPrazo({ card, onClick, draggable = false }: Props) {
  const { data: me } = useMe();
  const { data: tp } = useTransicoesPermitidas(card.id_prazo);
  const mut = useTransicao(card.id_prazo);
  const excluir = useExcluirPrazo();
  const [erro, setErro] = useState<string | null>(null);
  const isRecentlyMoved = useRecentlyMoved(card.id_prazo);

  const isTriagem = card.coluna === 'nao_feitos';

  // Peças (Fase 9) — só busca para cards em andamento (azul_claro / azul_escuro)
  const isEmAndamento = card.coluna === 'em_andamento';
  const { data: pecas } = usePecasStatus(isEmAndamento ? card.id_prazo : null);

  // Pipeline DOCAS — etapa macro (fresca do poll quando há; senão do payload do board)
  const etapaPipeline: EtapaPipeline | null = pecas?.etapa_pipeline ?? card.etapa_pipeline ?? null;
  const pecasStatus = pecas?.status ?? card.pecas_status ?? null;
  const gerandoPipe = pecasStatus === 'gerando';
  // Regerar petição (curto, ~5s) — speed-up de polling p/ a barra de progresso ficar viva.
  const gerandoRender = pecas?.gerando_tipo === 'render';
  usePecasStatusRapido(isEmAndamento ? card.id_prazo : null, gerandoRender);
  const iniciarPipe = useIniciarPipeline(card.id_prazo);
  const aprovarMapa = useAprovarMapa(card.id_prazo);
  const renderizarPeca = useRenderizarPeca(card.id_prazo);
  const setEtapaMut = useSetEtapa(card.id_prazo);
  async function handleRenderizar(e: React.MouseEvent) {
    e.stopPropagation();
    setErro(null);
    try { await renderizarPeca.mutateAsync(); }
    catch (err: any) { setErro(err?.status === 422 ? 'Anti-IA: revise bordões/contrast' : (err?.message ?? 'erro ao re-gerar')); }
  }
  async function handleGerarPeca(e: React.MouseEvent) {  // Refinamento → Redigindo (move, sem render)
    e.stopPropagation();
    setErro(null);
    try { await setEtapaMut.mutateAsync('redigindo'); }
    catch (err: any) { setErro(err?.message ?? 'erro'); }
  }
  async function handleDevolver(e: React.MouseEvent) {
    e.stopPropagation();
    setErro(null);
    try {
      if (etapaPipeline === 'preparo') {
        await setEtapaMut.mutateAsync('fila');
      } else if (etapaPipeline && ['mapa', 'mapa_aprovado', 'redigindo', 'revisao', 'concluido'].includes(etapaPipeline)) {
        // Sem coluna Refinamento: X em Redação volta direto para Preparo.
        await setEtapaMut.mutateAsync('preparo');
      } else {
        await mut.mutateAsync({ nome: 'desfazer-assumir' as EndpointTransicao });
      }
    } catch (err: any) {
      setErro(err?.status === 403 ? 'Sem permissão' : (err?.message ?? 'erro'));
    }
  }
  const queryClient = useQueryClient();
  const admin = me?.papel === 'admin';
  // Operar o pipeline (gerar/refinar/render/etapas) é restrito a admin/gerente —
  // o backend já recusa colaborador (403); aqui escondemos os botões para ele.
  const podePipeline = admin || me?.papel === 'gerente';
  const pode = usePode();
  // Refino: capacidade configurável pelo SU no painel Escritórios (checkbox),
  // como as demais permissões — espelha o require_capacidade('item.refino').
  const podeRefinar = pode('item.refino');
  const planoPro = usePlanoPro();   // freemium: free não gera peças (botão ⭐)
  const [showRefino, setShowRefino] = useState(false);

  // Dropdown inline do tipo de peça (abaixo de "Gerar rascunho"). Se o card já
  // trouxer um tipo_peca persistido (ex.: escolhido no popup "Novo Prazo" com
  // upload de autos), prevalece sobre a sugestão automática por tipo_prazo.
  const sugestaoPeca = (card.tipo_peca && TIPOS_PECA_BY_KEY[card.tipo_peca])
    || sugestaoPorTipoPrazo(card.tipo_prazo);
  const [pecaSelecionada, setPecaSelecionada] = useState<TipoPeca>(sugestaoPeca);
  // Páginas dos autos que o mapa NÃO deve ler por inteiro (ex.: anexo longo,
  // planilha, laudo de terceiro) — ver blocoGerarRascunho mais abaixo.
  const [paginasIgnorar, setPaginasIgnorar] = useState('');

  async function handleIniciarPipeline(e: React.MouseEvent) {
    e.stopPropagation();
    setErro(null);
    try {
      await iniciarPipe.mutateAsync({
        tipo_peca: pecaSelecionada.skill,
        peca_rotulo: pecaSelecionada.rotulo,
        paginas_ignorar: paginasIgnorar.trim() || undefined,
      });
    } catch (err: any) {
      setErro(err?.status === 400 ? 'Inclua o PDF do processo primeiro' : (err?.message ?? 'erro'));
    }
  }
  async function handleAprovarMapa(e: React.MouseEvent) {
    e.stopPropagation();
    setErro(null);
    try { await aprovarMapa.mutateAsync(); }
    catch (err: any) { setErro(err?.message ?? 'erro ao aprovar'); }
  }

  // ─── Aviso de conclusão (o card "pisca") ────────────────────────────────────
  // O usuário pode fechar o painel; o card segue pollando o status (5s). Quando a
  // geração TERMINA (gerando → pronto/erro), o card pisca + mostra um selo por ~12s,
  // pra avisar que o processamento acabou. Limpa ao clicar (abrir) ou no timeout.
  const statusAnteriorRef = useRef<string | undefined>(undefined);
  const [recemConcluido, setRecemConcluido] = useState<null | 'pronto' | 'erro'>(null);
  useEffect(() => {
    const atual = pecas?.status;
    const anterior = statusAnteriorRef.current;
    statusAnteriorRef.current = atual;
    if (anterior === 'gerando' && (atual === 'pronto' || atual === 'erro')) {
      setRecemConcluido(atual === 'pronto' ? 'pronto' : 'erro');
    }
  }, [pecas?.status]);
  useEffect(() => {
    if (!recemConcluido) return;
    const t = setTimeout(() => setRecemConcluido(null), 12000);
    return () => clearTimeout(t);
  }, [recemConcluido]);

  // Hook drag-and-drop (Fase 8). attributes/listeners no wrapper; transform
  // só aplica se draggable=true. Activation distance fica no DndContext do
  // KanbanSemanal (sensor PointerSensor com distance: 8) — clique curto NÃO
  // dispara drag, só abre o modal.
  const {
    attributes, listeners, setNodeRef, transform, isDragging,
  } = useDraggable({
    id: `card-${card.id_prazo}`,
    data: { idPrazo: card.id_prazo, etiqueta: card.etiqueta },
    disabled: !draggable,
  });
  const dragStyle = transform ? { transform: CSS.Translate.toString(transform) } : undefined;

  const cinza = card.etiqueta === 'cinza';
  const dEsc = card.dias_ate_vencimento;
  // Vermelho = urgente (≤3 dias ou vencido) | Amarelo = semana (4–7 dias) | Neutro = não urgente (≥8 dias)
  const corSLA = cinza ? undefined : (dEsc >= 8 ? undefined : dEsc > 3 ? 'amarelo' : 'vermelho');
  const corFaixa = cinza
    ? 'bg-etq-cinza'
    : (dEsc >= 8
      ? 'bg-slate-300 dark:bg-slate-600'
      : dEsc > 3
        ? 'bg-etq-amarelo'
        : 'bg-etq-vermelho');
  const textCorSLA = cinza
    ? 'text-slate-400 dark:text-slate-500 font-medium'
    : (dEsc >= 8
      ? 'text-slate-500 dark:text-slate-400 font-medium'
      : dEsc > 3
        ? 'text-amber-700 dark:text-yellow-400 font-semibold'
        : 'text-etq-vermelho dark:text-red-400 font-semibold');
  const ehUrgente = !cinza && dEsc <= 2;
  const verde = card.etiqueta === 'verde';

  // Audiência já realizada: data_evento (preenchido só p/ tipo_evento='audiencia')
  // no passado → card esmaecido no quadro. Comparação de strings ISO (TZ-safe).
  const hojeIso = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10);
  const ehAudiencia = !!card.data_evento;
  const audienciaPassada = ehAudiencia && card.data_evento! < hojeIso;

  const ringVerde = verde
    ? 'bg-emerald-50/50 dark:bg-emerald-950/20 ring-1 ring-emerald-250 dark:ring-emerald-800/60'
    : 'bg-white dark:bg-slate-900';
  const classeCinza = cinza ? 'card-cinza' : '';

  const dono = card.atribuido_a;

  // Transições disponíveis filtradas pelo backend
  const disponiveis = tp?.transicoes ?? [];

  async function dispara(nome: EndpointTransicao, e: React.MouseEvent) {
    e.stopPropagation();
    setErro(null);
    try {
      await mut.mutateAsync({ nome });
    } catch (err: any) {
      if (err.status === 403) setErro('Sem permissão');
      else if (err.status === 400) setErro('Transição inválida');
      else if (err.status === 409) setErro('Conflito — alguém alterou primeiro');
      else setErro(err.message);
    }
  }

  async function handleExcluir(e: React.MouseEvent) {
    e.stopPropagation();
    if (!window.confirm(
      `Excluir ${card.titulo || card.numero_processo}?\nO card sai do quadro (soft-cancel) e o evento do Google Agenda é removido.`,
    )) return;
    setErro(null);
    try {
      await excluir.mutateAsync(card.id_prazo);
    } catch (err: any) {
      if (err.status === 403) setErro('Sem permissão para excluir');
      else setErro(err?.message ?? 'erro ao excluir');
    }
  }

  // Botões MACRO (máquina de estados): Assumir (Triagem) e Protocolar (Pronto p/ protocolo).
  // Em "Em andamento" as ações são por etapa (bloco do pipeline). Drag desabilitado;
  // toda movimentação é por botão. NOTA: 'triar' não é exposto (assumir é direto).
  const botoes: { nome: EndpointTransicao; label: string; classe: string; title?: string; disabled?: boolean }[] = [];
  const temPdf = !!(card.tem_pdf ?? card.tem_peticao_drive);
  if (isTriagem && disponiveis.includes('assumir')) {
    // Assumir SEMPRE ativo (não exige o PDF dos autos): o card é assumido e vai
    // p/ Em Andamento/Fila, onde ali sim espera o PDF antes de gerar o rascunho.
    botoes.push({
      nome: 'assumir',
      label: 'Assumir',
      classe: 'flex-1 text-xs py-1 bg-blue-50 dark:bg-blue-950/20 hover:bg-blue-100 dark:hover:bg-blue-950/50 text-blue-700 dark:text-blue-400 border border-blue-200/55 dark:border-blue-900/40 rounded transition font-medium',
      title: 'Assumir o card para dar andamento no prazo',
    });
  }
  if (card.coluna === 'pronto_protocolo' && disponiveis.includes('protocolar'))
    botoes.push({ nome: 'protocolar', label: 'Protocolar',
      classe: 'flex-1 text-xs py-1 bg-green-50 dark:bg-green-950/20 hover:bg-green-100 dark:hover:bg-green-950/50 text-green-700 dark:text-green-400 border border-green-200/55 dark:border-green-900/40 rounded transition font-medium' });

  // Casos de Petição Inicial (origem='peticao_inicial') não passam pelo pipeline
  // DOCAS (split/OCR/classify de autos já existentes) — o rascunho nasce da
  // Análise inicial (documentos do cliente), disparada no painel de upload da
  // aba Petições Iniciais. "Gerar rascunho"/pipeline_docas não se aplica aqui.
  const isPeticaoInicial = card.origem === 'peticao_inicial';

  // Bloco "Gerar rascunho" + dropdown da peça — usado tanto na FILA (logo após
  // Assumir, com a peça já sugerida) quanto em Preparo. Clicar dispara o pipeline:
  // o backend passa o card para Preparo (etapa='preparo') e a barra de progresso
  // aparece. Modelo manual: NADA roda sem o usuário escolher a peça e clicar aqui.
  const blocoGerarRascunho = isPeticaoInicial ? (
    !planoPro ? (
      <div
        className="text-[11px] text-center py-1.5 px-2 bg-slate-100 dark:bg-slate-800/40 text-slate-400 dark:text-slate-500 border border-amber-400/40 rounded font-medium cursor-not-allowed"
        title="Análise inicial com IA é um recurso do plano Pro"
      >
        ⭐ Análise inicial (Pro)
      </div>
    ) : (
      // Botão REAL (o clique fica AQUI, não no corpo do card): abre o painel de
      // upload de documentos do caso, onde o usuário sobe os arquivos e dispara
      // a Análise inicial. `onClick` é o mesmo handler que a página passa.
      <button
        onClick={(e) => { e.stopPropagation(); onClick?.(); }}
        className="w-full text-[11px] text-center py-1.5 px-2 bg-violet-600 hover:bg-violet-700 text-white rounded font-medium transition"
        title="Abrir o painel para incluir os documentos do caso e rodar a Análise inicial"
      >
        📎 Incluir documentos do caso
      </button>
    )
  ) : (
    <div className="space-y-1">
      <div className="flex gap-1.5">
        {!planoPro ? (
          <button disabled
            className="flex-1 text-xs py-1 bg-slate-100 dark:bg-slate-800/40 text-slate-400 dark:text-slate-500 border border-amber-400/40 rounded font-medium cursor-not-allowed"
            title="Geração de peças com IA é um recurso do plano Pro">
            ⭐ Gerar rascunho (Pro)
          </button>
        ) : !temPdf ? (
          // Fila espera o PDF: sem os autos o card PARA aqui. O usuário clica no
          // card (abre o painel) e anexa o PDF; aí o "Gerar rascunho" libera.
          <button disabled
            className="flex-1 text-xs py-1 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400 border border-amber-300/60 dark:border-amber-800/40 rounded font-medium cursor-not-allowed"
            title="Clique no card para abrir o painel e anexar o PDF dos autos — depois gere o rascunho">
            📎 Anexe o PDF dos autos
          </button>
        ) : (
          <button onClick={handleIniciarPipeline} disabled={iniciarPipe.isPending}
            className="flex-1 text-xs py-1 bg-blue-50 dark:bg-blue-950/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200/55 dark:border-blue-900/40 rounded transition font-medium"
            title="Gerar mapa e rascunho da peça selecionada abaixo">
            {iniciarPipe.isPending ? '⏳ gerando…' : '📄 Gerar rascunho'}
          </button>
        )}
        {admin && (
          <button onClick={handleDevolver} disabled={setEtapaMut.isPending}
            className="text-[11px] py-1 px-2.5 text-blue-700 dark:text-blue-400 hover:text-red-600 dark:hover:text-red-400 transition border border-blue-200/55 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-950/20 rounded font-medium"
            title="Devolver">
            ✕
          </button>
        )}
      </div>
      {planoPro && (
        <select
          value={pecaSelecionada.rotulo}
          onChange={(e) => {
            const t = TIPOS_PECA.find((x) => x.rotulo === e.target.value);
            if (t) setPecaSelecionada(t);
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          disabled={iniciarPipe.isPending}
          className="w-full text-[11px] py-0.5 px-1 rounded border border-blue-200/55 dark:border-blue-900/40 bg-white dark:bg-slate-800 text-blue-900 dark:text-blue-200 outline-none focus:border-blue-500 cursor-pointer [color-scheme:light] dark:[color-scheme:dark] [&>optgroup]:bg-white dark:[&>optgroup]:bg-slate-800 [&>optgroup]:text-slate-500 dark:[&>optgroup]:text-slate-400 [&_option]:bg-white dark:[&_option]:bg-slate-800 [&_option]:text-slate-800 dark:[&_option]:text-slate-100"
          title="Tipo de peça a gerar (digite para pular para a opção)">
          {GRUPOS.map((g) => (
            <optgroup key={g} label={g}>
              {TIPOS_PECA.filter((t) => t.grupo === g).map((t) => (
                <option key={t.rotulo} value={t.rotulo}>
                  {t.rotulo === sugestaoPeca.rotulo ? `★ ${t.rotulo}` : t.rotulo}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      )}
      {planoPro && (
        <input
          type="text"
          value={paginasIgnorar}
          onChange={(e) => setPaginasIgnorar(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          disabled={iniciarPipe.isPending}
          placeholder="Páginas a ignorar (ex.: 400-450, 800)"
          className="w-full text-[11px] py-0.5 px-1 rounded border border-blue-200/55 dark:border-blue-900/40 bg-white dark:bg-slate-800 text-blue-900 dark:text-blue-200 outline-none focus:border-blue-500 placeholder:text-slate-400 dark:placeholder:text-slate-500"
          title="Intervalos de página dos autos que o mapa não precisa ler por inteiro (ex.: 400-450, 800)"
        />
      )}
    </div>
  );

  return (
    <>
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ type: "spring", stiffness: 380, damping: 30 }}
      whileHover={{ 
        y: -3, 
        scale: 1.015,
        boxShadow: "0 10px 20px -5px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.05)" 
      }}
      whileTap={{ scale: 0.985 }}
      ref={setNodeRef}
      style={dragStyle}
      {...attributes}
      {...listeners}
      onClick={() => {
        if (recemConcluido) setRecemConcluido(null);
        // Caso de petição inicial em andamento: o clique fica no botão
        // "📎 Incluir documentos do caso" (abaixo), NÃO no corpo do card — evita
        // o clique "invisível" no card que confundia o usuário. Demais cards:
        // clique padrão (abre o detalhe / painel).
        if (!(isPeticaoInicial && isEmAndamento)) onClick?.();
      }}
      className={`${classeCinza} ${audienciaPassada ? 'card-audiencia-passada' : ''} privacy-card ${ehUrgente ? 'is-urgente' : ''} ${
        recemConcluido === 'pronto' ? 'card-flash-pronto' : recemConcluido === 'erro' ? 'card-flash-erro' : ''
      } ${isRecentlyMoved ? 'card-flash-movido' : ''} flex border border-slate-200 dark:border-slate-800/80 rounded-md shadow-sm transition ${
        draggable ? 'cursor-grab active:cursor-grabbing' : (onClick && !(isPeticaoInicial && isEmAndamento) ? 'cursor-pointer' : 'cursor-default')
      } ${isDragging ? 'opacity-40' : ''} ${ringVerde} touch-none`}
    >
      <div className="p-2 flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1.5">
          <span className={`text-[11px] font-semibold flex items-center gap-1 ${textCorSLA}`}>
            {ehUrgente ? '⏰' : '📅'} {cinza ? `concluído` : `vence ${fmtData(card.data_vencimento, { curto: true })}`}
            {dEsc >= 0 && dEsc <= 2 && !cinza && ` (${dEsc}d!)`}
            {dEsc < 0 && !cinza && ` (atrasado ${Math.abs(dEsc)}d)`}
          </span>
          {dono && dono.nome !== me?.nome && (me?.papel === 'gerente' || me?.papel === 'admin') && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                setErro(null);
                try {
                  await api.avocar(card.id_prazo);
                  queryClient.invalidateQueries({ queryKey: ['prazos'] });
                  queryClient.invalidateQueries({ queryKey: ['pecas', card.id_prazo] });
                } catch (err: any) { setErro(err?.message ?? 'erro ao avocar'); }
              }}
              className="text-[10px] px-1.5 py-0.5 rounded border border-amber-300/60 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition"
              title={`Avocar este card (atual: ${dono.nome})`}
            >
              🫳 Avocar
            </button>
          )}
          {dono ? (
            <span className="inline-flex items-center gap-1" title={`Assumido por ${dono.nome}`}>
              <AvatarUsuario userId={dono.id} nome={dono.nome} corEtiqueta={dono.cor_etiqueta}
                tamanho="w-4 h-4" texto="text-[9px]" />
              <span className="text-[10px] font-medium text-meta-contrast privacy-target">
                {dono.nome.split(' ')[0]}
              </span>
            </span>
          ) : card.oab_origem ? (
            <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400"
              title={`Intimação recebida na OAB ${card.oab_origem}`}>
              ⚖️ OAB {card.oab_origem}
            </span>
          ) : (
            <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 italic">sem dono</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          <div className="titulo privacy-target text-sm font-semibold truncate flex-1 min-w-0 tracking-wide font-sans text-high-contrast">
            {card.titulo || card.numero_processo}
          </div>
          {(card.servicos_total ?? 1) > 1 && (
            <span
              className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-500/30"
              title="Este processo tem outros prazos em aberto — veja em Serviços do processo, no detalhe do card"
            >
              +{(card.servicos_total ?? 1) - 1} serviço{(card.servicos_total ?? 1) > 2 ? 's' : ''}
            </span>
          )}
          {card.tem_peticao_drive && (
            <span
              className="shrink-0 text-sm text-green-500"
              title="Autos no GDrive"
            >
              📎
            </span>
          )}
          {/* Badges de peças (Fase 9) */}
          {(pecas?.status === 'gerando' || pecas?.status === 'pendente_refinamento') && (
            <span className="shrink-0 text-[10px] animate-pulse text-blue-500" title={pecas?.status === 'pendente_refinamento' ? 'Refinamento em processamento…' : 'Gerando mapa e rascunho…'}>🔄</span>
          )}
          {pecas?.status === 'pronto' && pecas.mapa_drive_id && (
            <span className="shrink-0 text-[10px]" title="Mapa do Caso gerado">📋</span>
          )}
          {pecas?.status === 'pronto' && pecas.rascunho_drive_id && (
            <span className="shrink-0 text-[10px]" title={`Rascunho gerado${pecas.rascunho_versao > 1 ? ` (v${pecas.rascunho_versao})` : ''}`}>📝</span>
          )}
          {recemConcluido === 'pronto' && (
            <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 animate-pulse" title="Geração concluída agora">
              ✓ pronto
            </span>
          )}
          {recemConcluido === 'erro' && (
            <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-700 dark:text-red-300 border border-red-500/40 animate-pulse" title="A geração terminou com erro">
              ⚠ erro
            </span>
          )}
        </div>
        <div className="meta text-xs text-meta-contrast mt-0.5 font-sans flex items-center flex-wrap">
          {ehAudiencia && (
            <span className="mr-1 font-medium" title={audienciaPassada ? 'Audiência já realizada' : 'Audiência designada'}>
              {audienciaPassada && <span className="text-gray-400">realizada</span>}
            </span>
          )}
          {card.sigla_tribunal && (
            <span className="font-semibold text-slate-500 dark:text-slate-400 mr-1.5 uppercase bg-slate-100 dark:bg-slate-800 px-1 py-0.2 rounded text-[10px]">
              {card.sigla_tribunal}
            </span>
          )}
          <span className="font-semibold text-slate-700 dark:text-slate-200">{card.tipo_prazo}</span>
        </div>
        {cinza && (
          <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-slate-100 dark:border-slate-800/40">
            <span className="text-xs text-meta-contrast font-medium">
              ✓ protocolo {card.protocolado_em?.slice(11, 16)}
            </span>
            {/* Reabrir: devolve o card de Concluídos para Pronto p/ protocolar (com confirmação). */}
            {disponiveis.includes('desfazer_protocolo') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm('Voltar este card de "Concluídos" para "Pronto p/ protocolar"?'))
                    dispara('desfazer-protocolo', e);
                }}
                disabled={mut.isPending}
                title="Voltar para Pronto p/ protocolar"
                className="text-[11px] py-1 px-2.5 text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition border border-slate-200/55 dark:border-slate-700/40 bg-slate-50 dark:bg-slate-800/40 rounded font-medium"
              >
                ↩ Reabrir
              </button>
            )}
          </div>
        )}
        {(botoes.length > 0 || isTriagem || (card.coluna === 'pronto_protocolo' && disponiveis.includes('desfazer_pronto'))) && (
          <div className="flex gap-1.5 mt-2">
            {botoes.map((b) => (
              <button
                key={b.nome}
                onClick={(e) => { if (!b.disabled) dispara(b.nome, e); }}
                disabled={mut.isPending || !!b.disabled}
                className={b.classe}
                title={b.title}
              >
                {mut.isPending && !b.disabled ? '⏳' : b.label}
              </button>
            ))}
            {/* Restituir à lane em andamento (desfaz pronto p/ protocolo) */}
            {card.coluna === 'pronto_protocolo' && disponiveis.includes('desfazer_pronto') && (
              <button
                onClick={(e) => dispara('desfazer-pronto', e)}
                disabled={mut.isPending}
                title="Restituir card à lane Em andamento"
                className="text-[11px] py-1 px-2.5 text-slate-500 dark:text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 transition border border-slate-200/55 dark:border-slate-700/40 bg-slate-50 dark:bg-slate-800/40 rounded font-medium"
              >
                ✕
              </button>
            )}
            {/* TRIAGEM: Excluir disponível a admin e colaborador (overhaul v4). */}
            {isTriagem && (
              <button
                onClick={handleExcluir}
                disabled={excluir.isPending}
                title="Triagem - exclui o card"
                className="flex-1 text-xs py-1 bg-red-50 dark:bg-red-950/20 hover:bg-red-100 dark:hover:bg-red-950/50 text-red-700 dark:text-red-400 border border-red-200/55 dark:border-red-900/40 rounded transition"
              >
                {excluir.isPending ? '⏳' : '🗑 Excluir'}
              </button>
            )}
          </div>
        )}
        {/* ── EM ANDAMENTO — máquina de estados por etapa (drag off; tudo por botão) ── */}
        {isEmAndamento && (
          <div className="mt-2 pt-1.5 border-t border-slate-100 dark:border-slate-800/40" onClick={(e) => e.stopPropagation()}>
            {!podePipeline ? (
              /* Colaborador: vê o processo, mas SEM os botões que disparam o pipeline.
                 Mostra só o estado atual (somente leitura). */
              <div className="text-[10px] text-slate-500 dark:text-slate-400 font-medium truncate"
                   title="Em andamento — somente leitura">
                {gerandoPipe
                  ? `⏳ ${pecas?.progresso_msg ?? rotuloEtapa(etapaPipeline) ?? 'Processando…'}`
                  : (rotuloEtapa(etapaPipeline) || '📝 Em andamento')}
              </div>
            ) : gerandoPipe ? (
              /* PREPARO / pipeline em andamento → stepper + (na etapa blocos) preview ao vivo */
              <>
                <ProgressoPipeline
                  compact
                  etapa={pecas?.progresso_etapa ?? null}
                  pct={pecas?.progresso_pct ?? null}
                  msg={pecas?.progresso_msg ?? rotuloEtapa(etapaPipeline) ?? null}
                />
                <BlocosLivePreview idPrazo={card.id_prazo} ativo={pecas?.progresso_etapa === 'blocos' || pecas?.progresso_etapa === 'mapa'} />
              </>
            ) : (pecasStatus === 'pendente' || !etapaPipeline || etapaPipeline === 'fila') ? (
              /* FILA (após Assumir) → escolher a peça + "Gerar rascunho". Clicar
                 dispara o pipeline e leva o card para Preparo com a barra. Modelo
                 manual: nada roda sozinho (sem worker, sem cron). */
              blocoGerarRascunho
            ) : etapaPipeline === 'mapa' ? (
              /* REFINAMENTO → Refinar | Gerar a peça + Devolver (Azul) */
              <div className="flex gap-1 items-center">
                {podeRefinar && (
                  <button onClick={(e) => { e.stopPropagation(); setShowRefino(true); }}
                    className="flex-1 text-xs py-1 bg-blue-50 dark:bg-blue-950/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200/55 dark:border-blue-900/40 rounded transition font-medium"
                    title="Refinar — abre o terminal (chat) + os blocos JSON ao vivo">
                    ✏️ Refinar
                  </button>
                )}
                <button onClick={handleGerarPeca} disabled={setEtapaMut.isPending}
                  className="flex-1 text-xs py-1 bg-blue-50 dark:bg-blue-950/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200/55 dark:border-blue-900/40 rounded transition font-medium"
                  title="Finalizar o mapa do caso — passa o card para Redigindo">
                  {setEtapaMut.isPending ? '⏳' : 'Finalizar ▶'}
                </button>
                {admin && (
                  <button onClick={handleDevolver} disabled={setEtapaMut.isPending}
                    className="text-[11px] py-1 px-2.5 text-blue-700 dark:text-blue-400 hover:text-red-600 dark:hover:text-red-400 transition border border-blue-200/55 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-950/20 rounded font-medium"
                    title="Devolver para Preparo">
                    ✕
                  </button>
                )}
              </div>
            ) : etapaPipeline === 'preparo' ? (
              /* PREPARO → Gerar rascunho + dropdown do tipo de peça + Devolver */
              blocoGerarRascunho
            ) : !pecas?.docx_url ? (
              /* REDIGINDO sem .docx → Gerar petição + Devolver (Azul) */
              <div className="flex gap-1.5">
                <button onClick={handleRenderizar} disabled={renderizarPeca.isPending}
                  className="flex-1 text-xs py-1 bg-blue-50 dark:bg-blue-950/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200/55 dark:border-blue-900/40 rounded transition font-medium"
                  title="Gerar petição — renderiza o .docx a partir da última versão dos blocos">
                  {renderizarPeca.isPending ? '⏳ gerando…' : '📄 Gerar petição'}
                </button>
                {admin && (
                  <button onClick={handleDevolver} disabled={setEtapaMut.isPending}
                    className="text-[11px] py-1 px-2.5 text-blue-700 dark:text-blue-400 hover:text-red-600 dark:hover:text-red-400 transition border border-blue-200/55 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-950/20 rounded font-medium"
                    title="Devolver para Refinamento">
                    ✕
                  </button>
                )}
              </div>
            ) : gerandoRender ? (
              /* REDIGINDO + render em andamento → barra de progresso explicativa */
              <ProgressoPipeline
                etapa={pecas?.progresso_etapa ?? 'render'}
                pct={pecas?.progresso_pct ?? null}
                msg={pecas?.progresso_msg ?? null}
              />
            ) : (
              /* REDIGINDO com .docx → Visualizar + Marcar pronto + Devolver (Azul) */
              (() => {
                // Stale = docx no Drive está desatualizado em relação aos blocos JSON
                // (refino foi feito após o último render). Habilita só após "Regerar petição".
                const docxStale =
                  pecas?.docx_versao == null ||
                  (pecas?.rascunho_versao ?? 1) > pecas.docx_versao;
                return (
              <div className="flex gap-1.5 items-center">
                {docxStale ? (
                  <button
                    type="button"
                    disabled
                    className="flex-1 flex items-center justify-center gap-1 text-[11px] py-1 rounded bg-slate-100 dark:bg-slate-800/40 text-slate-400 dark:text-slate-500 border border-slate-200/70 dark:border-slate-700/40 font-medium cursor-not-allowed"
                    title="Regere a petição para refletir o último refino"
                  >
                    📄 Exibir petição
                  </button>
                ) : (
                  <a href={pecas.docx_url!} target="_blank" rel="noreferrer"
                     onClick={(e) => e.stopPropagation()}
                     className="flex-1 flex items-center justify-center gap-1 text-[11px] py-1 rounded bg-blue-50 dark:bg-blue-950/20 text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200/55 dark:border-blue-900/40 font-medium"
                     title="Abrir a petição (.docx) para revisar">
                    📄 Exibir petição
                  </a>
                )}
                {admin && (
                  <button onClick={handleRenderizar} disabled={renderizarPeca.isPending}
                    className={`text-[11px] py-1 px-1.5 bg-blue-50 dark:bg-blue-950/20 text-blue-800 dark:text-blue-300 border rounded font-medium ${docxStale ? 'border-amber-400/70 ring-1 ring-amber-400/40' : 'border-blue-200/55 dark:border-blue-900/40'}`}
                    title="Regerar petição">
                    {renderizarPeca.isPending ? '⏳' : '↻'}
                  </button>
                )}
                <button onClick={(e) => dispara('marcar-pronto', e)} disabled={mut.isPending}
                  className="flex-1 text-xs py-1 bg-blue-50 dark:bg-blue-950/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200/55 dark:border-blue-900/40 rounded transition font-medium"
                  title="Marcar pronto — vai para Pronto p/ protocolar">
                  {mut.isPending ? '⏳' : '✓ Pronto'}
                </button>
                {admin && (
                  <button onClick={handleDevolver} disabled={setEtapaMut.isPending}
                    className="text-[11px] py-1 px-2.5 text-blue-700 dark:text-blue-400 hover:text-red-600 dark:hover:text-red-400 transition border border-blue-200/55 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-950/20 rounded font-medium"
                    title="Devolver para Refinamento">
                    ✕
                  </button>
                )}
              </div>
                );
              })()
            )}
          </div>
        )}
        {erro && (
          <div className="mt-1.5 text-[10px] text-red-600 dark:text-red-400 font-medium">
            ⚠ {erro}
          </div>
        )}
      </div>
    </motion.div>
    {showRefino && createPortal(
      <RefinoSplit
        idPrazo={card.id_prazo}
        numeroProcesso={card.titulo || card.numero_processo}
        onClose={() => setShowRefino(false)}
        onFinalizado={() => {
          queryClient.invalidateQueries({ queryKey: ['pecas', card.id_prazo] });
          queryClient.invalidateQueries({ queryKey: ['arquivos', card.id_prazo] });
          queryClient.invalidateQueries({ queryKey: ['prazos'] });
        }}
      />,
      document.body,
    )}
    </>
  );
}
